import { Organization } from 'types/organization.type';
import { ReactFragment, ReactElement } from 'react';
import SubmissionListItem from '../application/Models/SubmissionListItem';
import moment, { Moment } from 'moment';
import { number } from 'yup';

export interface TypesData {
    actionTypes: any;
    constructionTypes: any;
    documentTypes: any;
    documentActionTypes: DocumentActionType;
    endorsementTypes: any;
    floodZoneTypes: any;
    locationClassTypes: any;
    logTypes: any;
    ownershipTypes: any;
    perilTypes: any;
    perilTypesList: any;
    propertyTypes: any;
    roofCoveringTypes: any;
    roofTypes: any;
    statusTypes: any;
    wiringTypes: any;
    roleTypes: any;
    organizationTypes: any;
    submissionUnderwritingStatusTypes: SubmissionUWStatusType;
    referralStatusTypes: any;
    referralReasonTypes: any;
    productLineTypes: Record<number, AmType>;
    locationSoftCostTypes: any;
    projectTypes: any;
}

export interface TypesState {
    data: TypesData;
    error: string;
    isLoading: boolean;
    hasResolved: boolean;
}

export interface LocationOptionsState {
    data: LocationOptionsData;
    error: string;
    isLoading: boolean;
    hasResolved: boolean;
}

export interface AmType {
    id: string;
    code: string;
    displayValue: string;
}

export interface SubmissionUWStatusType extends AmType {
    hasReferenceUser: boolean;
}
export interface PerilType extends AmType {
    productLineCode: string;
}
export interface DocumentType extends AmType {
    actionTypeId?: string;
    isRequired: boolean;
    requirementGroup?: number;
}

export interface DocumentActionType {
    id: number;
    documentTypeId: number;
    code: string;
    displayValue: string;
    actionTypeId: number;
    isRequired: boolean;
}

export interface IdResponse {
    id: string;
}

export interface CreateIdResponse {
    id: number;
    key: string;
}

export interface CreateIdsResponse {
    results: CreateIdResponse[];
}

export interface StatusType extends AmType {
    agentDisplayValue: string;
}

export enum ActiveProducerState {
    NO_ASSIGNED_PRODUCER,
    ACTIVE_USER_ASSIGNED_WITH_OPTIONS,
    DELEGATE_GRANTOR_ASSIGNED,
    ACTIVE_USER_ASSIGNED_NO_OPTIONS,
}

export interface ActiveUserProducer {
    fetchAvailableAgents(): Promise<void>;

    getProducer(): Organization | undefined;

    getId(): string | undefined;

    getEffectiveActiveProducer(assignedProducer: Organization): Organization | undefined;

    getCreateSubmissionSubHeader(assignedProducer: Organization, doSelectProducer: () => void): ReactFragment;
    getSubmissionDetailsSubHeader(assignedProducer: Organization): ReactFragment;

    getAvailableProducers(): Organization[];

    validateAssignedProducer(assignedProducer: Organization): Organization | undefined;

    getTemplateQualifier(fileType: string, availableQualifiers: string[] | undefined): string;

    getNoSubmissionsText(assignedProducer: Organization): ReactFragment;
}

export interface LocationOption {
    code: string;
    displayValue: string;
    optionTypeCode: string;
    orderNumber: number;
}

export interface AllLocationOptionsResponse {
    options: Record<string, LocationOption[]>;
}

export type LocationOptionsData = Record<string, Record<string, string>>;

export interface AllTypesResponse {
    [key: string]: AmType[] | DocumentType[] | StatusType[] | SubmissionUWStatusType[];

    actionTypes: AmType[];
    constructionTypes: AmType[];
    documentTypes: DocumentType[];
    endorsementTypes: AmType[];
    floodZoneTypes: AmType[];
    locationClassTypes: AmType[];
    logTypes: AmType[];
    ownershipTypes: AmType[];
    perilTypes: AmType[];
    propertyTypes: AmType[];
    roofCoveringTypes: AmType[];
    roofTypes: AmType[];
    statusTypes: StatusType[];
    wiringTypes: AmType[];
    roleTypes: AmType[];
    organizationalTypes: AmType[];
    submissionUnderwritingStatusTypes: SubmissionUWStatusType[];
    referralStatusTypes: AmType[];
    referralReasonTypes: AmType[];
}

export enum SubmissionTabTypes {
    STATUS = 'STATUS',
    EXPIRING = 'EXPIRING',
    PROPERTY = 'PROPERTY',
}

export interface SubmissionDataTabsDefinitionTabFilter {
    type: SubmissionTabTypes;
    statusList?: string[];
    propertyFilter?: string;
    propertyValue?: any;
    numDays?: number;
    startDate?: Moment;
    endDate?: Moment;
}

export interface CustomTabRendererProps {
    submissions: SubmissionListItem[];
    isLoading: boolean;
    error: string;
    columnDefs: any[];
    productLineFilter?: string[];
    emptyText: string;
}

export interface SubmissionDataTabsDefinitionTab {
    tabName: string;
    emptyText?: string;
    disableCount?: boolean;
    isInForce?: boolean;
    filters: SubmissionDataTabsDefinitionTabFilter[];
    additionalColumns?: any[];
    childTabs?: SubmissionDataTabsDefinitionTab[];
    customRenderer?: (props: CustomTabRendererProps) => ReactElement | null;
}

export interface SubmissionDataTabsDefinition {
    defaultTab?: string;
    tabs: SubmissionDataTabsDefinitionTab[];
}

export interface SubmissionDataTab {
    tabName: string;
    tabSubmissions: SubmissionListItem[];
    additionalColumns: any[];
    emptyText: string;
    addCount: boolean;
    childTabs: SubmissionDataTab[];
    customRenderer?: (props: CustomTabRendererProps) => ReactElement | null;
}

export interface CatscoreLocationPeril {
    averageAnnualLoss: number;
    capitalCost: number;
    cededLoss: number;
    perilName: string;
    reinsurancePremium: number;
    reinsuranceMargin: number;
}

export interface AonLocation {
    catScore?: CatscoreLocation;
    wfScore: number;
    floodScore: number;
    distanceToWFHigh: number;
    locationName: string;
    locationId: string;
}

export interface CatscoreLocation {
    distanceToCoast: number;
    convectiveStormScore: number;
    perilScores: CatscoreLocationPeril[];
    locationName: string;
    locationId: string;
}

export interface TaxesAndFees {
    stateCode: string;

    policyType: string;

    coverageType: string;

    taxRate: number;

    stampingFeeRate: number;

    municipalityFeeRate: number;

    firemarshalTaxRate: number;

    county: string;

    stateTaxRate: number;

    stateStampingFeeRate: number;

    feesTaxed: boolean;

    roundingPrecisionDigits: number;
}

export interface Tag {
    tagId: number;
    displayValue: string;
    tagTypeCode: string;
}
